<template>
  <Loader class="lead-dashboard" theme="default" :value="promises.fetchOne">
    <template v-if="item.id">
      <Hero :lead="item" class="lead-dashboard__header">
        <template #header>
          <HeaderComponent
            :breadcrumb="breadcrumbs"
            class="lead-hero__header"
            style="background-color: transparent; position: relative; z-index: 1"
          >
            <template #right>
              <div class="lead-hero__actions">
                <Actions placement="lead" :query="{ lead: id }">
                  <template #bottom>
                    <ButtonComponent
                      tag="router-link"
                      :to="{
                        name: 'contact-edit',
                        params: { id: $path('contact.id', item) },
                        query: { returnTo: $route.fullPath, current: item.reference.id },
                      }"
                      :label="$t('editContact')"
                      :icon="['fal', 'pen']"
                      class="btn"
                    />
                    <ButtonComponent
                      :label="$t(!item.isArchived ? 'archive' : 'restore')"
                      :icon="`fa-pro-light:${item.isArchived ? 'undo' : 'box-archive'}`"
                      confirm
                      @click="onArchiveClick"
                    />
                  </template>
                </Actions>
                <Actions
                  placement="lead-create"
                  :label="$t('add')"
                  icon="fa-pro-light:plus"
                  theme="primary"
                  :query="{ lead: id }"
                >
                  <template #bottom>
                    <TaskCreateButton
                      :reference="{ onModel: 'Lead', ref: id }"
                      theme="default"
                      icon="fa-pro-light:list-check"
                      :label="$tc('task')"
                    />
                    <ButtonComponent
                      :label="$tc('comment', 2)"
                      icon="fa-pro-light:comments"
                      @click="scrollTo('.comments')"
                    />
                    <ButtonComponent
                      :label="$t('activityLog')"
                      icon="fa-pro-light:clock-rotate-left"
                      @click="scrollTo('.lead-activities')"
                    />
                    <ButtonComponent
                      :label="$tc('contract', 2)"
                      icon="fa-pro-light:file-contract"
                      @click="scrollTo('.contract-list')"
                    />
                  </template>
                </Actions>
              </div>
            </template>
          </HeaderComponent>
        </template>
      </Hero>
      <StatusBar v-model="item.status" :reference="reference" :lead="id" />

      <div class="lead-dashboard__content">
        <ScrollNavigation
          :value="[
            { selector: '.lead-hero', label: this.$t('clientCard') },
            { selector: '.lead-activities', label: this.$t('activityLog') },
            { selector: '.comments', label: this.$t('clientDialog') },
            { selector: '.task-list', label: this.$tc('task', 2) },
            { selector: '.contact-contracts', label: this.$tc('contract', 2) },
          ]"
          :component="CardComponent"
        />
        <Activities :lead="id" :scope="$path('reference', item)" />

        <Comments :title="$tc('comment', 2)" :value="item.comments" @submit="comment" :promise="commentPromise" />

        <Tasks :reference="{ onModel: 'Lead', ref: id }" />

        <Contracts :reference="{ onModel: 'Lead', ref: id }" :contact="item.contact.id" />
      </div>
    </template>
  </Loader>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import Model from '@/models/Lead/Mutate'
import fetch from '@/fetch'
import ResidentialApi from '@/api/Residential'
import CommentApi from '@/api/Comment'
import ContactApi from '@/api/Contact'
import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { LoaderComponent as Loader } from 'vue-elder-loader'
import RemoveComponent from '@/components/Remove'
import ChecklistComponent from '@/components/Select/Checklist'
import ContactCard from '@/components/Contact/Card'
import Contracts from '@/components/Contract/Contracts'
import Comments from '@/components/Comment/List'
import HeaderComponent from '@/components/Header'
import FileList from '@/components/Comment/FileList'
import TaskCreateButton from '@/components/Task/CreateButton'
import Actions from '@/components/Actions'
import EditableBoard from '@/components/EditableBoard.vue'
import Activities from '@/components/Lead/Activities.vue'
import Card from '@/components/Card-v2.vue'
import ProjectSelector from '@/views/Lead/ProjectSelector.vue'
import StatusBar from '@/components/Lead/StatusBar.vue'
import Hero from '@/components/Lead/Hero'
import { LeadPathByModel, getListLabel } from '@/components/Lead/utils'
import ScrollNavigation from '@/components/ScrollNavigation.vue'
import Tasks from '@/components/Task/List/Card'

export default {
  props: {
    id: String,
    reference: String,
  },
  data() {
    return {
      data: Model(),
      removeDialog: false,
      commentPromise: null,
      validator: {
        rules: {
          name: 'required',
          email: 'required|email',
          phone: 'mobile',
        },
        labels: 'labels',
        data: 'data',
      },
      CardComponent: Card,
    }
  },
  watch: {
    id: {
      handler(id = '') {
        return this.fetch({ id, reference: this.reference })
      },
      immediate: true,
    },
    item: {
      handler(value) {
        this.data = Model(value)
        if (!value.isProcessed && value.id) this.mutate([{ id: this.id, isProcessed: true }, { setPromise: false }])
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState('Lead', ['item', 'promises']),
    ...mapi18n('labels', ['email', 'name', 'phone']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingLead() {
        return this.$t('loading', { resource: this.$tc('lead', 1) }) + '...'
      },
    }),
    isNew() {
      return !this.id
    },
    onModel() {
      return this.$path('item.reference.onModel') || 'Project'
    },
    context() {
      return LeadPathByModel[this.onModel]
    },
    breadcrumbs() {
      return [
        {
          title:
            (JSON.parse(sessionStorage.getItem(`${this.context}:category`)) || {}).label || getListLabel(this.onModel),
          action: { name: `${this.context}s` },
        },
        {
          title: this.$path('item.reference.name'),
          action: {
            name: `${this.context}-dashboard`,
            params: { id: this.$path('item.reference.id') },
          },
        },
        {
          title: this.$tc('lead', 2),
          action: { name: `${this.context}-leads`, params: { reference: this.$path('item.reference.id') } },
        },
        { title: this.$metaInfo.title },
      ]
    },
  },
  methods: {
    ...mapActions('Lead', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Lead', ['set', 'addComment']),
    submit() {
      this.mutate(this.data)
    },
    comment(data) {
      this.commentPromise = CommentApi.mutate({
        reference: { onModel: 'Lead', ref: this.id },
        ...data,
      }).then(data => this.addComment(data))
    },
    onArchiveClick() {
      const { id, isArchived } = this.item
      fetch(`/api/lead/${id}/${isArchived ? 'unarchive' : 'archive'}`, { method: 'POST' }).then(
        () => (this.item.isArchived = !this.item.isArchived),
      )
    },
    scrollTo(selector) {
      const el = document.querySelector(selector)
      if (!el) return

      el.scrollIntoView({ behavior: 'smooth' })
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createLead') : this.$path('item.contact.name') || this.i18n.loadingLead,
    }
  },
  components: {
    StatusBar,
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    CheckboxComponent,
    ChecklistComponent,
    ContactCard,
    Comments,
    RemoveComponent,
    Card,
    TaskCreateButton,
    FileList,
    Contracts,
    Actions,
    EditableBoard,
    Activities,
    ProjectSelector,
    ScrollNavigation,
    Hero,
    Loader,
    Tasks,
  },
}
</script>

<style lang="scss">
.lead-dashboard {
  background-color: $background-grey;

  &__content {
    display: grid;
    gap: 2rem;
    grid-template-areas:
      'statusbar statusbar'
      'comments activities'
      'tasks tasks'
      'contracts contracts';

    grid-template-columns: 1fr 1fr;

    max-width: var(--container-limit);
    padding-inline: var(--container-outer-spacing);
    padding-bottom: var(--container-outer-spacing);
    margin-inline: auto;
  }

  --scroll-margin: 100px;

  .lead-hero {
    grid-area: hero;
  }

  .lead-activities {
    grid-area: activities;

    scroll-margin: var(--scroll-margin);
  }

  .comments {
    grid-area: comments;

    scroll-margin: var(--scroll-margin);
  }

  .tasks-list-card {
    grid-area: tasks;

    scroll-margin: var(--scroll-margin);
  }

  .contract-list {
    grid-area: contracts;

    scroll-margin: var(--scroll-margin);
  }

  .scrollnavigation {
    display: none;
    grid-area: navigation;

    .card-v2__content {
      --card-padding: 1rem;
    }
  }

  .lead-statusbar {
    grid-area: statusbar;

    position: sticky;
    top: 0;
    z-index: 99;
  }
}
</style>
